import React from "react";
import { useNavigate } from "react-router";
import { FiSearch, FiX } from "react-icons/fi";
import { RiChat4Line } from "react-icons/ri";

import { DateTime } from "luxon";

import useSearchThreadsQuery from "./useSearchThreadsQuery";
import modalStyles from "../../Modal/Modal.module.scss";
import styles from "./SearchModal.module.scss";

function formatRelativeDate(timestamp: string): string {
  const date = DateTime.fromISO(timestamp);
  const now = DateTime.now();
  if (date.hasSame(now, "day")) return "Just now";
  if (date.hasSame(now.minus({ days: 1 }), "day")) return "Yesterday";
  if (date.year !== now.year) return date.toFormat("MMM d, yyyy");
  return date.toFormat("MMM d");
}

interface SearchModalProps {
  onClose: () => void;
}

export default function SearchModal({ onClose }: SearchModalProps) {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");
  const { data: results } = useSearchThreadsQuery(query);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleThreadClick = (threadId: string) => {
    navigate(`/chats/${threadId}`);
    onClose();
  };

  return (
    <div className={modalStyles.backdrop} onClick={handleBackdropClick}>
      <div className={modalStyles.modal}>
        <div className={styles.searchHeader}>
          <FiSearch className={styles.searchIcon} />
          <input
            autoFocus
            type="text"
            className={styles.searchInput}
            placeholder="Search chats by title"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className={styles.closeButton} onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className={styles.results}>
          {results && results.length === 0 && (
            <p className={styles.hint}>No results found</p>
          )}
          {results &&
            results.length > 0 &&
            results.map((thread) => (
              <button
                key={thread.id}
                className={styles.resultItem}
                onClick={() => handleThreadClick(thread.id)}
              >
                <RiChat4Line className={styles.resultIcon} />
                <span className={styles.resultName}>{thread.name}</span>
                {thread.timestamp && (
                  <span className={styles.resultDate}>
                    {formatRelativeDate(thread.timestamp)}
                  </span>
                )}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}
